import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface Notification {
  id: number;
  type: 'success' | 'error' | 'info' | 'warning';
  titre: string;
  message: string;
  duree: number;
  actions?: Array<{
    id: 'confirm' | 'cancel';
    libelle: string;
    variante?: 'primary' | 'secondary';
  }>;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private notifications: Notification[] = [];
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  private compteurId = 0;
  private sujetsConfirmation = new Map<number, Subject<boolean>>();

  notifications$ = this.notificationsSubject.asObservable();

  private afficher(type: Notification['type'], titre: string, message: string, duree: number = 5000): void {
    const notification: Notification = {
      id: ++this.compteurId,
      type,
      titre,
      message,
      duree
    };

    this.notifications.push(notification);
    this.notificationsSubject.next([...this.notifications]);

    if (duree > 0) {
      setTimeout(() => this.retirer(notification.id), duree);
    }
  }

  succes(message: string, titre: string = 'Succès'): void {
    this.afficher('success', titre, message);
  }

  erreur(message: string, titre: string = 'Erreur'): void {
    this.afficher('error', titre, message, 7000);
  }

  information(message: string, titre: string = 'Information'): void {
    this.afficher('info', titre, message);
  }

  avertissement(message: string, titre: string = 'Attention'): void {
    this.afficher('warning', titre, message, 6000);
  }

  confirmer(
    message: string,
    titre: string = 'Confirmation',
    libelleConfirmer: string = 'Confirmer',
    libelleAnnuler: string = 'Annuler'
  ): Observable<boolean> {
    const idNotification = ++this.compteurId;
    const sujet = new Subject<boolean>();
    this.sujetsConfirmation.set(idNotification, sujet);

    const notification: Notification = {
      id: idNotification,
      type: 'warning',
      titre,
      message,
      duree: 0,
      actions: [
        { id: 'cancel', libelle: libelleAnnuler, variante: 'secondary' },
        { id: 'confirm', libelle: libelleConfirmer, variante: 'primary' },
      ],
    };

    this.notifications.push(notification);
    this.notificationsSubject.next([...this.notifications]);

    return sujet.asObservable();
  }

  declencherAction(id: number, action: 'confirm' | 'cancel'): void {
    const sujet = this.sujetsConfirmation.get(id);
    if (sujet) {
      sujet.next(action === 'confirm');
      sujet.complete();
      this.sujetsConfirmation.delete(id);
    }
    this.retirer(id);
  }

  retirer(id: number): void {
    const sujet = this.sujetsConfirmation.get(id);
    if (sujet) {
      sujet.next(false);
      sujet.complete();
      this.sujetsConfirmation.delete(id);
    }
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.notificationsSubject.next([...this.notifications]);
  }

  effacer(): void {
    this.notifications = [];
    this.notificationsSubject.next([]);
  }
}
